import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Replay as ResetIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import { LoadingSpinner, StatusChip } from '../../components/atoms'
import { BaseTable } from '../../components/TableComponents'
import ImportResultsDialog from '../../components/ImportResultsDialog'
import QAImportMappingConfig from './QAImportMappingConfig'
import sharedStyles from './qualityAssurance/SettingsQA.module.scss'
import styles from './SettingsImportQA.module.scss'

const STATUS_STYLES = {
  ready: { chipColor: 'success', labelKey: 'importQA.status.ready' },
  already_imported: { chipColor: 'warning', labelKey: 'importQA.status.alreadyImported' },
  unmatched: { chipColor: 'error', labelKey: 'importQA.status.notInDb' },
  no_call_id: { chipColor: 'neutral', labelKey: 'importQA.status.noCallId' },
}

export default function SettingsImportQA() {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)

  const [step, setStep] = useState('upload')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [overwriteExisting, setOverwriteExisting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false)

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return
    if (!selectedFile.name.endsWith('.csv')) {
      setError(t('importQA.csvFileRequired'))
      return
    }
    setFile(selectedFile)
    setError(null)
    setPreview(null)
    setImportResult(null)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const droppedFile = e.dataTransfer.files[0]
    handleFileSelect(droppedFile)
  }

  const handleUploadPreview = async () => {
    if (!file) return
    setLoading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await authenticatedFetch(apiEndpoints.qaImportPreview, {
        method: 'POST',
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to preview CSV')
      }

      setPreview(data.data)
      setStep('preview')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmImport = async () => {
    if (!file) return
    setStep('importing')
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('overwriteExisting', overwriteExisting.toString())

      const response = await authenticatedFetch(apiEndpoints.qaImportConfirm, {
        method: 'POST',
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to import QA results')
      }

      setImportResult(data.data)
      setStep('results')
      if (data.data.rows) {
        setResultsDialogOpen(true)
      }
    } catch (err) {
      setError(err.message)
      setStep('preview')
    }
  }

  const handleReset = () => {
    setStep('upload')
    setFile(null)
    setPreview(null)
    setImportResult(null)
    setError(null)
    setOverwriteExisting(false)
    setResultsDialogOpen(false)
    /* istanbul ignore next -- ref may not be attached during reset from non-upload steps */
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getImportCount = () => {
    return overwriteExisting
      ? preview.readyToImport + preview.alreadyImported
      : preview.readyToImport
  }

  const previewColumns = [
    { id: 'rowIndex', label: '#' },
    { id: 'callId', label: t('importQA.table.callId'), renderCell: (row) => row.callId || '-' },
    {
      id: 'agentEmail',
      label: t('importQA.table.agent'),
      renderCell: (row) => row.agentEmail || '-',
    },
    {
      id: 'campaignName',
      label: t('importQA.table.campaign'),
      renderCell: (row) => row.campaignName || '-',
    },
    { id: 'callDate', label: t('importQA.table.date'), renderCell: (row) => row.callDate || '-' },
    {
      id: 'callGrade',
      label: t('importQA.table.grade'),
      renderCell: (row) => row.callGrade || '-',
    },
    {
      id: 'result',
      label: t('importQA.table.result'),
      renderCell: (row) =>
        row.result ? (
          <StatusChip
            size="small"
            label={row.result}
            color={row.result === 'SUCCESSFUL CALL' ? 'success' : 'error'}
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'status',
      label: t('importQA.table.status'),
      renderCell: (row) => {
        const statusStyle = STATUS_STYLES[row.status] || STATUS_STYLES.no_call_id
        return (
          <StatusChip size="small" label={t(statusStyle.labelKey)} color={statusStyle.chipColor} />
        )
      },
    },
  ]

  return (
    <Box className={styles.root}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          style={{ marginBottom: 16, borderRadius: 12 }}
        >
          {error}
        </Alert>
      )}

      {/* Mapping Config */}
      {step === 'upload' && <QAImportMappingConfig />}

      {/* Upload Step */}
      {step === 'upload' && (
        <Card className={styles.card}>
          <CardContent className={sharedStyles.cardContent}>
            <Typography variant="h6" className={styles.cardTitle} gutterBottom>
              {t('importQA.uploadTitle')}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
              {t('importQA.uploadDescription')}
            </Typography>

            <Box
              className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault()
                setDragActive(true)
              }}
              onDragLeave={() => setDragActive(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
              <UploadIcon
                style={{ fontSize: 48, color: 'var(--color-text-light)', marginBottom: 16 }}
              />
              <Typography
                variant="h6"
                style={{ color: 'var(--color-text-primary)', marginBottom: 8 }}
              >
                {file ? file.name : t('importQA.dropZoneText')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : t('importQA.dropZoneBrowse')}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="flex-end" style={{ marginTop: 24 }}>
              <Button
                variant="contained"
                onClick={handleUploadPreview}
                disabled={!file || loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />
                }
                className={`${styles.actionButton} ${styles.primaryButton}`}
              >
                {loading ? t('importQA.processing') : t('importQA.uploadPreview')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Preview Step */}
      {step === 'preview' && preview && (
        <>
          {/* Summary Stats */}
          <Card className={styles.card}>
            <CardContent className={sharedStyles.cardContent}>
              <Typography variant="h6" className={styles.cardTitle} gutterBottom>
                {t('importQA.summaryTitle')}
              </Typography>

              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.totalRows')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}
                    >
                      {preview.totalRows}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.readyToImport')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-success)' }}
                    >
                      {preview.readyToImport}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.alreadyImported')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-warning)' }}
                    >
                      {preview.alreadyImported}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.skipped')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-text-secondary)' }}
                    >
                      {preview.rowsWithoutCallId + preview.unmatchedCalls}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Detail breakdown */}
              <Box
                style={{
                  padding: 16,
                  backgroundColor: 'var(--color-bg-light)',
                  borderRadius: 12,
                  marginBottom: 16,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.rowsWithCallId')}: <strong>{preview.rowsWithCallId}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.rowsWithoutCallId')}:{' '}
                      <strong>{preview.rowsWithoutCallId}</strong> ({t('importQA.willBeSkipped')})
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.matchedInDb')}: <strong>{preview.matchedCalls}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.notFoundInDb')}: <strong>{preview.unmatchedCalls}</strong> (
                      {t('importQA.willBeSkipped')})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.campaigns')}:{' '}
                      <strong>{preview.campaigns.join(', ') || 'N/A'}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('importQA.dateRange')}:{' '}
                      <strong>
                        {preview.dateRange.earliest && preview.dateRange.latest
                          ? `${preview.dateRange.earliest} to ${preview.dateRange.latest}`
                          : 'N/A'}
                      </strong>
                    </Typography>
                    {preview.duplicateCallIds?.length > 0 && (
                      <Typography variant="body2" style={{ color: 'var(--color-warning)' }}>
                        {t('importQA.duplicateCallIds')}:{' '}
                        <strong>{preview.duplicateCallIds.length}</strong>
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* Warnings */}
              {preview.rowsWithoutCallId > 0 && (
                <Alert
                  severity="warning"
                  style={{ marginBottom: 8, borderRadius: 12 }}
                  icon={<WarningIcon />}
                >
                  {t('importQA.noCallIdWarning', { count: preview.rowsWithoutCallId })}
                </Alert>
              )}
              {preview.unmatchedCalls > 0 && (
                <Alert
                  severity="warning"
                  style={{ marginBottom: 8, borderRadius: 12 }}
                  icon={<WarningIcon />}
                >
                  {t('importQA.unmatchedWarning', { count: preview.unmatchedCalls })}
                </Alert>
              )}

              {/* Overwrite toggle */}
              {preview.alreadyImported > 0 && (
                <Box
                  style={{
                    padding: 16,
                    backgroundColor: 'var(--color-bg-light)',
                    borderRadius: 12,
                    border: '1px solid var(--color-border)',
                    marginBottom: 16,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={overwriteExisting}
                        onChange={(e) => setOverwriteExisting(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {t('importQA.overwriteLabel', { count: preview.alreadyImported })}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {overwriteExisting
                            ? t('importQA.overwriteOn')
                            : t('importQA.overwriteOff')}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Preview Table */}
          <Card className={styles.card}>
            <CardContent className={sharedStyles.cardContent}>
              <Typography variant="h6" className={styles.cardTitle} gutterBottom>
                {t('importQA.previewTitle', { count: preview.previewRows.length })}
              </Typography>

              <BaseTable
                columns={previewColumns}
                data={preview.previewRows}
                tableProps={{ size: 'small' }}
              />

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" style={{ gap: 12, marginTop: 24 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  className={`${styles.actionButton} ${styles.outlinedButton}`}
                >
                  {t('importQA.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmImport}
                  disabled={
                    preview.readyToImport === 0 &&
                    !(overwriteExisting && preview.alreadyImported > 0)
                  }
                  className={`${styles.actionButton} ${styles.primaryButton}`}
                >
                  {getImportCount() !== 1
                    ? t('importQA.importCountPlural', { count: getImportCount() })
                    : t('importQA.importCount', { count: getImportCount() })}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Importing Step */}
      {step === 'importing' && (
        <Card className={styles.card}>
          <CardContent style={{ padding: 48, textAlign: 'center' }}>
            <LoadingSpinner size={48} message={t('importQA.importing')} />
            <Typography variant="body2" color="textSecondary">
              {t('importQA.importingDescription')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Results Step */}
      {step === 'results' && importResult && (
        <Card className={styles.card}>
          <CardContent className={sharedStyles.cardContent}>
            <Box display="flex" alignItems="center" style={{ marginBottom: 24 }}>
              <SuccessIcon
                style={{ fontSize: 32, color: 'var(--color-success)', marginRight: 12 }}
              />
              <Typography variant="h6" className={styles.cardTitle}>
                {t('importQA.importComplete')}
              </Typography>
            </Box>

            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importQA.imported')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-success)' }}
                  >
                    {importResult.imported}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importQA.updated')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}
                  >
                    {importResult.updated}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importQA.skipped')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-text-secondary)' }}
                  >
                    {importResult.skipped}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importQA.errors')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 700,
                      color:
                        importResult.errors.length > 0
                          ? 'var(--color-error)'
                          : 'var(--color-text-secondary)',
                    }}
                  >
                    {importResult.errors.length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {importResult.errors.length > 0 && (
              <Alert severity="warning" style={{ marginBottom: 16, borderRadius: 12 }}>
                {t('importQA.errorsWarning', { count: importResult.errors.length })}
                <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  {importResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>
                      Row {err.rowIndex} (Call ID: {err.callId}): {err.error}
                    </li>
                  ))}
                  {importResult.errors.length > 5 && (
                    <li>{t('importQA.andMore', { count: importResult.errors.length - 5 })}</li>
                  )}
                </ul>
              </Alert>
            )}

            <Box display="flex" justifyContent="flex-end" style={{ gap: 12 }}>
              {importResult.rows && (
                <Button
                  variant="outlined"
                  onClick={() => setResultsDialogOpen(true)}
                  startIcon={<ViewIcon />}
                  className={`${styles.actionButton} ${styles.outlinedButton}`}
                >
                  {t('importQA.viewDetails')}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleReset}
                startIcon={<ResetIcon />}
                className={`${styles.actionButton} ${styles.primaryButton}`}
              >
                {t('importQA.uploadAnother')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <ImportResultsDialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
        result={importResult}
      />
    </Box>
  )
}
